import { Inject, Injectable } from '@angular/core';
import { BrandingV2Interface, BrandingV2Service } from '@galaxy/partner';
import { OrderFulfillmentServiceInterface, OrderFulfillmentTableRow } from '@vendasta/orders';
import { DomainKeyValue, KeyValueTypes, SearchTaskRequestInterface, TaskSdkService } from '@vendasta/task';
import { TaskInterface } from '@vendasta/task/';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { PARTNER_ID_INJECTION_TOKEN } from '../../../core/constants';
import { OrderConfirmationItem } from '../interface';

export const VENDOR_TEMPLATE_TYPE = 'VendorTemplate';
export const PRODUCT_ID_METADATA_KEY = 'ProductId';
export const FULFILLING_PARTNER_ID_METADATA_KEY = 'FulfillingPartnerId';

@Injectable()
export class FulfillmentOrderConfirmationService implements OrderFulfillmentServiceInterface {
  public readonly tableRows$: Observable<OrderFulfillmentTableRow[]>;
  public readonly shouldShowOrderFulfillmentSection$: Observable<boolean>;
  private orderItems$$: BehaviorSubject<OrderConfirmationItem[]> = new BehaviorSubject<OrderConfirmationItem[]>([]);

  constructor(
    private taskSdkService: TaskSdkService,
    @Inject(PARTNER_ID_INJECTION_TOKEN) private readonly partnerId$: Observable<string>,
    private brandingService: BrandingV2Service,
  ) {
    this.tableRows$ = this.orderItems$$.pipe(
      startWith([]),
      switchMap((orderItems) => this.buildTableRows(orderItems)),
    );
    this.shouldShowOrderFulfillmentSection$ = this.tableRows$.pipe(map((tableRows) => Boolean(tableRows?.length > 0)));
  }

  /**
   * Determines the Fulfilling Partner Id from metadata. If it does not exist, uses the namespace to determine
   * @param template
   * @private
   */
  private static getFulfillingPartnerIdForTemplate(template: TaskInterface): string {
    let fulfillingPartnerId: string = DomainKeyValue?.fromKeyValue(template.metadata)
      ?.get(FULFILLING_PARTNER_ID_METADATA_KEY, KeyValueTypes.STRING)
      ?.toString();
    if (!fulfillingPartnerId) {
      fulfillingPartnerId = template.identity?.namespace?.split('/')[1] || '';
    }
    return fulfillingPartnerId;
  }

  setOrderItems(items: OrderConfirmationItem[]): void {
    this.orderItems$$.next(items);
  }

  private buildTableRows(items: OrderConfirmationItem[]): Observable<OrderFulfillmentTableRow[]> {
    return this.fetchVendorTemplates(items).pipe(
      switchMap((vendorTemplates) => {
        if (vendorTemplates?.length > 0) {
          return this.fetchNamesForFulfillingPartners(vendorTemplates || []).pipe(
            switchMap((brandingMap) => {
              const appIdToNameMap: Map<string, string> = new Map<string, string>(
                items.map((item) => [item.appId, item.name]),
              );
              return this.convertVendorTemplateToOrderFulfillmentTableRow(vendorTemplates, brandingMap, appIdToNameMap);
            }),
          );
        }
        return of([]);
      }),
    );
  }

  private fetchVendorTemplates(items: OrderConfirmationItem[]): Observable<TaskInterface[]> {
    const productIds: string[] = items.map((item) => item.appId);
    const metadata: DomainKeyValue = new DomainKeyValue().set(
      PRODUCT_ID_METADATA_KEY,
      productIds,
      KeyValueTypes.STRING,
    );
    const request: SearchTaskRequestInterface = {
      types: [VENDOR_TEMPLATE_TYPE],
      cursor: '0',
      pageSize: 250,
      metadata: metadata.toKeyValue(),
    };
    return this.taskSdkService.search(request).pipe(map((resp) => resp.tasks));
  }

  private fetchNamesForFulfillingPartners(templates: TaskInterface[]): Observable<Map<string, string>> {
    const fulfillingPartnerIds: string[] =
      templates?.map((template) => FulfillmentOrderConfirmationService.getFulfillingPartnerIdForTemplate(template)) ||
      [];
    const uniqueFulfillingPartnerIds: string[] = Array.from(new Set(fulfillingPartnerIds))?.filter(Boolean);
    if (uniqueFulfillingPartnerIds?.length > 0) {
      return forkJoin(
        uniqueFulfillingPartnerIds.map((fulfillPartnerId) => this.brandingService.getBranding(fulfillPartnerId)),
      ).pipe(
        map((branding: BrandingV2Interface[]) => {
          const fulfillPartnerIdToPartnerNameMap: Map<string, string> = new Map<string, string>();
          for (let i = 0; i < uniqueFulfillingPartnerIds.length; i++) {
            fulfillPartnerIdToPartnerNameMap.set(uniqueFulfillingPartnerIds[i], branding[i]?.name || '');
          }
          return fulfillPartnerIdToPartnerNameMap;
        }),
      );
    }
    return of(new Map<string, string>());
  }

  private convertVendorTemplateToOrderFulfillmentTableRow(
    vendorTemplates: TaskInterface[],
    brandingMap: Map<string, string>,
    appIdToNameMap: Map<string, string>,
  ): Observable<OrderFulfillmentTableRow[]> {
    return this.partnerId$.pipe(
      map((currentPartnerId) => {
        return vendorTemplates?.map((template) => {
          const fulfillPartnerId: string =
            FulfillmentOrderConfirmationService.getFulfillingPartnerIdForTemplate(template);
          const productId: string = DomainKeyValue?.fromKeyValue(template.metadata)
            ?.get(PRODUCT_ID_METADATA_KEY, KeyValueTypes.STRING)
            ?.toString();
          return {
            template: template,
            serviceProviderName: brandingMap.get(fulfillPartnerId) || '',
            productName: appIdToNameMap.get(productId) || '',
            isCurrentPartner: fulfillPartnerId === currentPartnerId,
          } as OrderFulfillmentTableRow;
        });
      }),
    );
  }
}
